import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiSearch, FiArrowRight, FiAlertCircle, FiMail, FiPhone } = FiIcons;

const NotFound = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real application, you would implement search functionality here
      // For now, we'll redirect to the home page or services page based on common search terms
      const query = searchQuery.toLowerCase();
      if (query.includes('service') || query.includes('consulting')) {
        window.location.href = '/services';
      } else if (query.includes('contact')) {
        window.location.href = '/contact';
      } else if (query.includes('about')) {
        window.location.href = '/about';
      } else {
        window.location.href = '/';
      }
    }
  };

  const quickLinks = [
    { name: 'Home', path: '/', description: 'Return to our homepage' },
    { name: 'Services', path: '/services', description: 'Explore our consulting services' },
    { name: 'Strategic Planning', path: '/services/strategic-planning', description: 'Market entry and growth strategies' },
    { name: 'Digital Transformation', path: '/services/digital-transformation', description: 'Technology integration solutions' },
    { name: 'About Us', path: '/about', description: 'Learn about our company' },
    { name: 'Blog & Insights', path: '/blog', description: 'Industry insights and analysis' },
    { name: 'Contact', path: '/contact', description: 'Get in touch with our team' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* 404 Graphic */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-32 h-32 bg-purple-100 rounded-full mb-6">
                <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-purple-600" />
              </div>
              <h1 className="text-6xl lg:text-8xl font-bold text-purple-900 mb-4 font-figtree">
                404
              </h1>
              <h2 className="text-2xl lg:text-3xl font-semibold text-gray-900 mb-4 font-serif">
                Page Not Found
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                We're sorry, but the page you're looking for doesn't exist. It may have been moved, deleted, or you may have entered the wrong URL.
              </p>
            </div>

            {/* Search Bar */}
            <div className="mb-12">
              <form onSubmit={handleSearch} className="max-w-md mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for services, insights, or information..."
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                  />
                  <SafeIcon 
                    icon={FiSearch} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-700 text-white px-4 py-2 rounded-md hover:bg-purple-800 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/"
                className="bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-800 transition-colors inline-flex items-center justify-center"
              >
                <SafeIcon icon={FiHome} className="mr-2 w-5 h-5" />
                Return Home
              </Link>
              <Link
                to="/contact"
                className="border-2 border-purple-700 text-purple-700 px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 hover:text-white transition-colors inline-flex items-center justify-center"
              >
                <SafeIcon icon={FiMail} className="mr-2 w-5 h-5" />
                Contact Support
              </Link>
            </div>
          </motion.div>

          {/* Quick Links Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <h3 className="text-2xl font-semibold text-gray-900 mb-8 font-serif">
              Popular Pages
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickLinks.map((link, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow group"
                >
                  <Link to={link.path} className="block">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                      {link.name}
                    </h4>
                    <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                      {link.description}
                    </p>
                    <div className="inline-flex items-center text-purple-700 font-medium text-sm group-hover:text-purple-800 transition-colors">
                      Visit Page
                      <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white border border-gray-200 rounded-xl p-8"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-6 font-serif">
              Need Additional Help?
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              If you can't find what you're looking for, our team is here to help. Contact us directly for personalized assistance with your business transformation needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="flex items-center justify-center space-x-3 text-gray-700">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-purple-600" />
                <span>+27 11 555 0123</span>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-700">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-purple-600" />
                <span>info@rsasolutions.co.za</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;